import { useScroll, useMotionValueEvent } from 'framer-motion';
import { useCallback, useState } from 'react';
import type { RefObject } from 'react';

interface UseHorizontalScrollOptions {
  containerRef: RefObject<HTMLElement | null>;
  totalItems: number;
}

export function useHorizontalScroll({ containerRef, totalItems }: UseHorizontalScrollOptions) {
  const { scrollXProgress } = useScroll({ container: containerRef });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // スクロール位置の監視
  useMotionValueEvent(scrollXProgress, 'change', (value) => {
    const index = Math.round(value * (totalItems - 1));
    setCurrentIndex(index);
    setCanScrollLeft(value > 0);
    setCanScrollRight(value < 1);
  });

  // インデックスへスクロール
  const scrollToIndex = useCallback((index: number) => {
    if (!containerRef.current) return;
    const itemWidth = containerRef.current.scrollWidth / totalItems;
    const targetScroll = itemWidth * index;
    containerRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, [containerRef, totalItems]);

  // 前へスクロール
  const scrollPrev = useCallback(() => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  }, [currentIndex, scrollToIndex]);

  // 次へスクロール
  const scrollNext = useCallback(() => {
    const newIndex = Math.min(totalItems - 1, currentIndex + 1);
    scrollToIndex(newIndex);
  }, [currentIndex, scrollToIndex, totalItems]);

  return {
    scrollXProgress,
    currentIndex,
    canScrollLeft,
    canScrollRight,
    scrollToIndex,
    scrollPrev,
    scrollNext
  };
}